import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { FileText, RefreshCw } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api`;

const typeStyleMap = {
  IN: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-400/20',
  OUT: 'bg-red-500/15 text-red-300 ring-1 ring-red-400/20',
  REIMBURSE: 'bg-sky-500/15 text-sky-300 ring-1 ring-sky-400/20',
};

const formatCurrency = (value) =>
  new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const formatDate = (value) => {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('id-ID', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const buildQueryString = (startDate, endDate) => {
  const params = new URLSearchParams();
  if (startDate) params.set('start_date', startDate);
  if (endDate) params.set('end_date', endDate);
  return params.toString();
};

export default function PettyCashDeclaration() {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchDeclaration = useCallback(async () => {
    setLoading(true);
    try {
      const queryString = buildQueryString(startDate, endDate);
      const endpoint = queryString
        ? `${API}/petty-cash/declaration?${queryString}`
        : `${API}/petty-cash/declaration`;
      const { data } = await axios.get(endpoint, { withCredentials: true });
      setReport(data || null);
    } catch (error) {
      console.error('Failed to fetch petty cash declaration:', error);
      alert(error.response?.data?.detail || 'Gagal mengambil data deklarasi petty cash.');
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    fetchDeclaration();
  }, [fetchDeclaration]);

  const summary = report?.summary || {
    closing_balance: 0,
    total_in: 0,
    total_reimburse: 0,
    total_out: 0,
    total_entries: 0,
  };
  const period = report?.period || { start_date: null, end_date: null };
  const entries = report?.entries || [];

  return (
    <DashboardLayout>
      <Header title="Deklarasi Petty Cash" />
      <div className="space-y-5 p-6 lg:p-8" data-testid="petty-cash-declaration-page">
        <div className="rounded-sm border border-white/10 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.16),transparent_32%),linear-gradient(135deg,rgba(255,255,255,0.03),rgba(9,9,11,0.82))] p-5">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between">
            <div>
              <Badge className="mb-3 rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/10">
                Laporan Kasir
              </Badge>
              <h2 className="text-2xl font-semibold tracking-tight text-white">Deklarasi Petty Cash</h2>
              <p className="mt-1 text-sm text-zinc-400">
                Rekap saldo awal, transaksi masuk/keluar, dan saldo akhir petty cash berdasarkan periode.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-[160px_160px_auto]">
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Dari</p>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(event) => setStartDate(event.target.value)}
                  className="h-11 rounded-sm border-white/10 bg-black/25 text-white"
                  data-testid="petty-cash-declaration-start-date"
                />
              </div>
              <div>
                <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-zinc-500">Sampai</p>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(event) => setEndDate(event.target.value)}
                  className="h-11 rounded-sm border-white/10 bg-black/25 text-white"
                  data-testid="petty-cash-declaration-end-date"
                />
              </div>
              <Button
                type="button"
                onClick={fetchDeclaration}
                disabled={loading}
                className="self-end rounded-sm bg-[#D4AF37] px-6 text-black hover:bg-[#C59A27]"
                data-testid="petty-cash-declaration-filter-btn"
              >
                <RefreshCw className="mr-2 h-4 w-4" strokeWidth={1.7} />
                Terapkan
              </Button>
            </div>
          </div>
          <p className="mt-4 text-xs text-zinc-500">
            Periode laporan: {period.start_date || 'Semua data'} s/d {period.end_date || 'Semua data'}
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
          <div className="rounded-sm border border-white/10 bg-zinc-950/40 p-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Saldo Saat Ini</p>
            <p className="mt-3 text-2xl font-semibold text-[#F3D77A]">{formatCurrency(summary.closing_balance)}</p>
          </div>
          <div className="rounded-sm border border-emerald-400/20 bg-emerald-500/10 p-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total In</p>
            <p className="mt-3 text-2xl font-semibold text-emerald-200">{formatCurrency(summary.total_in)}</p>
          </div>
          <div className="rounded-sm border border-sky-400/20 bg-sky-500/10 p-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total Reimburse</p>
            <p className="mt-3 text-2xl font-semibold text-sky-200">{formatCurrency(summary.total_reimburse)}</p>
          </div>
          <div className="rounded-sm border border-red-400/20 bg-red-500/10 p-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total Out</p>
            <p className="mt-3 text-2xl font-semibold text-red-200">{formatCurrency(summary.total_out)}</p>
          </div>
          <div className="rounded-sm border border-white/10 bg-zinc-950/40 p-5">
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-zinc-500">Total Transaksi</p>
            <p className="mt-3 text-2xl font-semibold text-white">{summary.total_entries || 0}</p>
          </div>
        </div>

        <div className="overflow-hidden rounded-sm border border-white/10 bg-zinc-950/45">
          <div className="grid grid-cols-[1fr_.8fr_.8fr_.9fr_.8fr_1.2fr] gap-4 border-b border-white/10 bg-white/[0.02] px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] text-zinc-500">
            <span>No. Transaksi</span>
            <span>Tipe</span>
            <span>Nominal</span>
            <span>Saldo Akhir</span>
            <span>Tanggal</span>
            <span>Catatan</span>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="h-8 w-8 animate-spin rounded-full border-2 border-[#D4AF37] border-t-transparent" />
            </div>
          ) : entries.length > 0 ? (
            <div className="max-h-[560px] divide-y divide-white/5 overflow-auto">
              {entries.map((entry) => {
                const entryType = String(entry.entry_type || 'IN').toUpperCase();
                const signedAmount = Number(entry.signed_amount ?? (entryType === 'IN' ? entry.amount : -entry.amount));
                return (
                  <div key={entry.id} className="grid grid-cols-[1fr_.8fr_.8fr_.9fr_.8fr_1.2fr] gap-4 px-4 py-3 text-sm hover:bg-white/[0.02]">
                    <p className="font-medium text-zinc-200">{entry.transaction_number || '-'}</p>
                    <p>
                      <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${typeStyleMap[entryType] || 'bg-white/10 text-zinc-200'}`}>
                        {entry.type_label || entryType}
                      </span>
                    </p>
                    <p className={`font-semibold ${signedAmount >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                      {signedAmount >= 0 ? '+' : '-'}{formatCurrency(Math.abs(signedAmount))}
                    </p>
                    <p className="font-semibold text-[#F3D77A]">{formatCurrency(entry.balance_after)}</p>
                    <p className="text-zinc-300">{formatDate(entry.entry_date)}</p>
                    <div className="min-w-0">
                      {entry.notes ? <p className="truncate text-zinc-300">{entry.notes}</p> : <p className="text-zinc-600">-</p>}
                      {entry.created_by ? <p className="mt-1 truncate text-xs text-zinc-600">By: {entry.created_by}</p> : null}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <FileText className="mb-3 h-10 w-10 text-zinc-700" strokeWidth={1.5} />
              <p className="text-sm font-medium text-white">Tidak ada data deklarasi pada periode ini</p>
              <p className="mt-1 text-sm text-zinc-500">Ubah periode lalu klik Terapkan untuk menampilkan data.</p>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
